import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/* helpers */
const sameId = (a, b) => String(a || "") === String(b || "");
const toStr = (x) => (x == null ? "" : String(x));

const matchesFilters = (item, args = {}) => {
  const {
    q = "",
    branchId = "",
    counterId = "",
    vendorId = "",
    diamondId = "",
    shape = "",
    clarity = "",
    color = "",
    sieve = "",
    size = "",
    dateFrom = "",
    dateTo = "",
  } = args || {};

  if (branchId && !sameId(item.branchId, branchId)) return false;
  if (counterId && !sameId(item.counterId, counterId)) return false;
  if (vendorId && !sameId(item.vendorId, vendorId)) return false;
  if (diamondId && !sameId(item.diamondId, diamondId)) return false;
  if (shape && !sameId(item.shape, shape)) return false;
  if (clarity && !sameId(item.clarity, clarity)) return false;
  if (color && !sameId(item.color, color)) return false;
  if (sieve && !sameId(item.sieve, sieve)) return false;
  if (size && !sameId(item.size, size)) return false;

  if (dateFrom && new Date(item.createdAt) < new Date(dateFrom)) return false;
  if (dateTo && new Date(item.createdAt) > new Date(dateTo)) return false;

  if (q) {
    const needle = toStr(q).toLowerCase();
    const hay = [
      item.diamondName,
      item.shapeName,
      item.clarityName,
      item.colorName,
      item.sieveName,
      item.sizeName,
      item.branchName,
      item.counterName,
      item.vendorName,
      item.description,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(needle)) return false;
  }
  return true;
};

const incrementTotals = (draft, by = 1) => {
  if (draft?.total != null) {
    draft.total += by;
    const lim = draft?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};
const decrementTotals = (draft, by = 1) => {
  if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    const lim = draft?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};
const addItemsIntoListDraft = (draft, args, items = []) => {
  if (!Array.isArray(draft?.data)) return;
  if (args.page && Number(args.page) !== 1) {
    incrementTotals(draft, items.length);
    return;
  }
  draft.data.unshift(...items);
  if (args.limit) draft.data.splice(Number(args.limit));
  incrementTotals(draft, items.length);
};

export const looseDiamondApi = createApi({
  reducerPath: "looseDiamondApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["LooseDiamondEntry", "LooseDiamondReport"],
  endpoints: (builder) => ({
    /* LIST */
    // GET /loose-diamonds
    getLooseDiamondEntries: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 20,
          q = "",
          branchId = "",
          counterId = "",
          vendorId = "",
          diamondId = "",
          shape = "",
          clarity = "",
          color = "",
          sieve = "",
          size = "",
          dateFrom = "",
          dateTo = "",
          sortBy = "createdAt",
          sortOrder = "desc",
        } = args;

        const params = {
          page,
          limit,
          q,
          branchId,
          counterId,
          vendorId,
          diamondId,
          shape,
          clarity,
          color,
          sieve,
          size,
          dateFrom,
          dateTo,
          sortBy,
          sortOrder,
        };
        Object.keys(params).forEach((k) => (params[k] ? null : delete params[k]));

        return { url: "/loose-diamonds", method: "GET", params };
      },
      providesTags: ["LooseDiamondEntry"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page, a.limit, a.q,
          a.branchId, a.counterId, a.vendorId,
          a.diamondId, a.shape, a.clarity, a.color, a.sieve, a.size,
          a.dateFrom, a.dateTo, a.sortBy, a.sortOrder,
        ].join("|");
      },
      keepUnusedDataFor: 60,
    }),

    /* GET ONE */
    getLooseDiamondEntryById: builder.query({
      query: (id) => `/loose-diamonds/${id}`,
      providesTags: (_res, _err, id) => [{ type: "LooseDiamondEntry", id }],
    }),

    /* CREATE (single/bulk) */
    // POST /loose-diamonds/create   { items:[...] } or array
    addLooseDiamondEntry: builder.mutation({
      query: (payload) => {
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
          ? payload.items
          : [payload];
        return {
          url: "/loose-diamonds/create",
          method: "POST",
          body: { items },
        };
      },
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const created = Array.isArray(res?.data) ? res.data : [];
          if (!created.length) return;

          const allArgs = looseDiamondApi.util.selectCachedArgsForQuery(
            getState(),
            "getLooseDiamondEntries"
          );
          for (const args of allArgs) {
            const addable = created.filter((it) => matchesFilters(it, args));
            if (!addable.length) continue;
            dispatch(
              looseDiamondApi.util.updateQueryData("getLooseDiamondEntries", args, (draft) => {
                addItemsIntoListDraft(draft, args, addable);
              })
            );
          }
        } catch {}
      },
    }),

    /* UPDATE */
    // PUT /loose-diamonds/:id
    updateLooseDiamondEntry: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/loose-diamonds/${id}`,
        method: "PUT",
        body: payload,
      }),
      async onQueryStarted({ id, payload }, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = looseDiamondApi.util.selectCachedArgsForQuery(
          getState(),
          "getLooseDiamondEntries"
        );

        for (const args of allArgs) {
          patches.push(
            dispatch(
              looseDiamondApi.util.updateQueryData("getLooseDiamondEntries", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const i = draft.data.findIndex((x) => String(x._id) === String(id));
                if (i >= 0) draft.data[i] = { ...draft.data[i], ...payload };
              })
            )
          );
        }

        patches.push(
          dispatch(
            looseDiamondApi.util.updateQueryData("getLooseDiamondEntryById", id, (draft) => {
              if (draft?.data) Object.assign(draft.data, payload);
            })
          )
        );

        try {
          const { data: res } = await queryFulfilled;
          const updated = res?.data;
          if (!updated) return;

          for (const args of allArgs) {
            dispatch(
              looseDiamondApi.util.updateQueryData("getLooseDiamondEntries", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const i = draft.data.findIndex((x) => String(x._id) === String(id));
                if (i >= 0) draft.data[i] = { ...draft.data[i], ...updated };
              })
            );
          }
          dispatch(
            looseDiamondApi.util.updateQueryData("getLooseDiamondEntryById", id, (draft) => {
              if (draft?.data) Object.assign(draft.data, updated);
            })
          );
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* DELETE */
    // DELETE /loose-diamonds/:id
    deleteLooseDiamondEntry: builder.mutation({
      query: (id) => ({ url: `/loose-diamonds/${id}`, method: "DELETE" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const rollbacks = [];
        const allArgs = looseDiamondApi.util.selectCachedArgsForQuery(
          getState(),
          "getLooseDiamondEntries"
        );
        for (const args of allArgs) {
          rollbacks.push(
            dispatch(
              looseDiamondApi.util.updateQueryData("getLooseDiamondEntries", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((it) => String(it._id) !== String(id));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          rollbacks.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* BULK DELETE */
    // POST /loose-diamonds/bulk/delete  { ids:[...] }
    bulkDeleteLooseDiamondEntries: builder.mutation({
      query: (ids) => ({
        url: `/loose-diamonds/bulk/delete`,
        method: "POST",
        body: { ids },
      }),
      async onQueryStarted(ids, { dispatch, queryFulfilled, getState }) {
        const rollbacks = [];
        const allArgs = looseDiamondApi.util.selectCachedArgsForQuery(
          getState(),
          "getLooseDiamondEntries"
        );
        for (const args of allArgs) {
          rollbacks.push(
            dispatch(
              looseDiamondApi.util.updateQueryData("getLooseDiamondEntries", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((it) => !ids.includes(String(it._id)));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          rollbacks.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* REPORT */
    getLooseDiamondReport: builder.query({
      query: (params) => ({ url: "/loose-diamonds/report", method: "GET", params }),
      transformResponse: (res) => res?.data || res,
      providesTags: [{ type: "LooseDiamondReport", id: "LIST" }],
    }),
  }),
});

export const {
  useGetLooseDiamondEntriesQuery,
  useGetLooseDiamondEntryByIdQuery,
  useAddLooseDiamondEntryMutation,
  useUpdateLooseDiamondEntryMutation,
  useDeleteLooseDiamondEntryMutation,
  useBulkDeleteLooseDiamondEntriesMutation,
  useGetLooseDiamondReportQuery,
} = looseDiamondApi;

export default looseDiamondApi;
